/**
 * Client-side tier display metadata — the SINGLE source of truth for how plans
 * are presented across the UI (badge, billing page, tier comparison table,
 * data-source cards, import page, upgrade nudges, onboarding tour).
 *
 * Why one file: the audit in 2026-05-25 found drift between BillingPage's
 * local TIER_CARDS, sales-brochure.html's hardcoded HTML, planMeta's
 * TIER_META, and DocsPage's inline mentions — same product, four
 * slightly-different stories. This module now owns all of it:
 *   • Per-tier metadata (price, blurb, limits, badge colors)
 *   • A canonical FEATURE_ROWS list — one entry per capability, with a
 *     cell per tier. Drives the comparison table on Billing + the
 *     onboarding close slide + the public sales brochure.
 *   • Convenience projections (per-tier feature highlights) derived from
 *     the same source so they can't drift.
 *
 * Server enforcement still lives in server/src/lib/plans.ts. The limit
 * NUMBERS here mirror those — if you change one, change both.
 *
 * Per [[tier-surfaces-are-conversion-levers]]: every limit shown to a user
 * is paired with what upgrading unlocks.
 */

export type PlanName = 'free' | 'team' | 'business' | 'enterprise';

export const PLAN_ORDER: PlanName[] = ['free', 'team', 'business', 'enterprise'];

// ── Per-tier metadata ───────────────────────────────────────────────────────

export interface TierMeta {
  name: PlanName;
  label: string;
  /** Short price string for chips/cards. Full pricing lives on the billing page. */
  price: string;
  priceUnit: string;
  /** One-line positioning shown on the per-tier card. */
  blurb: string;
  /** Optional eyebrow tagline above the price (e.g. "Most popular"). */
  tagline?: string;
  /** Tailwind classes for the tier badge chip. */
  badgeClasses: string;
  /** Tailwind classes for the larger BillingPage current-plan header chip. */
  chipClasses: string;
  seatLimit: number | null;     // null = unlimited
  jiraLimit: number | null;     // null = unlimited
  importRowLimit: number;       // rows per file import; capped at 5,000 today
  /** Feature flags used by gated UI nudges. Mirror server FEATURE_GATES. */
  features: {
    intelligence: boolean;
    export: boolean;
    customFields: boolean;
    branding: boolean;
    jiraWriteBack: boolean;
  };
}

export const TIER_META: Record<PlanName, TierMeta> = {
  free: {
    name: 'free',
    label: 'Free',
    price: '$0',
    priceUnit: 'forever',
    blurb: 'Try Avium with your real data. No credit card.',
    badgeClasses: 'bg-slate-100 text-slate-600 border border-slate-200',
    chipClasses:  'bg-gray-100 text-gray-700 border-gray-200',
    seatLimit: 5,
    jiraLimit: 2,
    importRowLimit: 1000,
    features: { intelligence: false, export: false, customFields: false, branding: false, jiraWriteBack: false },
  },
  team: {
    name: 'team',
    label: 'Team',
    price: '$10',
    priceUnit: '/seat/mo',
    blurb: 'For growing teams ready to make data-driven planning decisions.',
    tagline: 'No more spreadsheets',
    badgeClasses: 'bg-teal-100 text-brand-primary-dark border border-teal-200',
    chipClasses:  'bg-brand-primary-light text-brand-primary-dark border-teal-200',
    seatLimit: 25,
    jiraLimit: 10,
    importRowLimit: 5000,
    features: { intelligence: true, export: false, customFields: false, branding: false, jiraWriteBack: false },
  },
  business: {
    name: 'business',
    label: 'Business',
    price: '$16',
    priceUnit: '/seat/mo',
    blurb: 'For teams that want AI on top — and the ability to push insights back to Jira.',
    badgeClasses: 'bg-teal-100 text-teal-700 border border-teal-200',
    chipClasses:  'bg-teal-50 text-teal-700 border-teal-200',
    seatLimit: 100,
    jiraLimit: null,
    importRowLimit: 5000,
    features: { intelligence: true, export: true, customFields: true, branding: true, jiraWriteBack: true },
  },
  enterprise: {
    name: 'enterprise',
    label: 'Enterprise',
    price: 'Custom',
    priceUnit: '',
    blurb: 'For orgs with procurement, SSO, and dedicated support requirements.',
    badgeClasses: 'bg-amber-100 text-amber-800 border border-amber-200',
    chipClasses:  'bg-amber-50 text-amber-800 border-amber-200',
    seatLimit: null,
    jiraLimit: null,
    importRowLimit: 5000,
    features: { intelligence: true, export: true, customFields: true, branding: true, jiraWriteBack: true },
  },
};

// ── Canonical feature row list ──────────────────────────────────────────────
//
// One row per customer-facing capability, with a cell per tier. Everything
// the customer compares is derived from this list:
//   • The per-tier "what you get" cards on BillingPage
//   • The tier comparison table on the onboarding close slide
//   • The public sales-brochure cards (manually mirrored as HTML — keep in
//     sync when you edit either)
//
// Rows are GROUPED so the comparison table can section them ("Foundations",
// "Avium Signals", etc.). Display order is the array order.

export type FeatureGroup = 'Foundations' | 'Avium Signals' | 'Planning' | 'AI' | 'Sync & Export' | 'Enterprise';

export interface FeatureCell {
  included: boolean;
  /** Optional note shown next to the indicator (e.g. "Up to 25", "All signals"). */
  note?: string;
  /** When set, render as a "coming soon" cell rather than a hard ✗. */
  roadmap?: boolean;
}

export interface TierFeatureRow {
  feature: string;
  group: FeatureGroup;
  /** Optional one-liner shown under the feature name in the comparison table. */
  detail?: string;
  cells: Record<PlanName, FeatureCell>;
}

const incl  = (note?: string): FeatureCell => ({ included: true, ...(note ? { note } : {}) });
const excl  = (): FeatureCell => ({ included: false });
const future = (): FeatureCell => ({ included: false, roadmap: true });

export const TIER_FEATURE_ROWS: TierFeatureRow[] = [
  // ── Foundations (everyone gets these to some degree) ──────────────────────
  { feature: 'Seats', group: 'Foundations', cells: {
      free:       incl('Up to 5'),
      team:       incl('Up to 25'),
      business:   incl('Up to 100'),
      enterprise: incl('Unlimited'),
  }},
  { feature: 'Connected Jira instances', group: 'Foundations', cells: {
      free:       incl('Up to 2'),
      team:       incl('Up to 10'),
      business:   incl('Unlimited'),
      enterprise: incl('Unlimited'),
  }},
  { feature: 'Excel imports', group: 'Foundations', detail: 'Bring data in from any spreadsheet.', cells: {
      free:       incl('1,000 rows'),
      team:       incl('5,000 rows'),
      business:   incl('5,000 rows'),
      enterprise: incl('5,000 rows'),
  }},
  { feature: 'Workload board, phases, sprint matrix, list', group: 'Foundations', cells: {
      free: incl(), team: incl(), business: incl(), enterprise: incl(),
  }},

  // ── Avium Signals (the engine) ─────────────────────────────────────────────
  { feature: 'Avium Signals — the proprietary detection engine', group: 'Avium Signals',
    detail: 'Sprint risk, over-allocation, velocity drift, blocked work, data quality.',
    cells: { free: excl(), team: incl('All signals'), business: incl('All signals'), enterprise: incl('All signals') } },
  { feature: 'Configurable reports', group: 'Avium Signals',
    detail: 'Lead Time, Time in Status, Reopen Rate, Flow Efficiency, Hand-off Cost.',
    cells: { free: excl(), team: incl(), business: incl(), enterprise: incl() } },
  { feature: 'Workflow Sankey + Ping-Pong + Sprint Scope-Change', group: 'Avium Signals',
    detail: 'Process mining and anti-pattern detection over the real status paths.',
    cells: { free: excl(), team: incl(), business: incl(), enterprise: incl() } },
  { feature: 'Saved views + org defaults', group: 'Avium Signals', cells: {
      free: excl(), team: incl(), business: incl(), enterprise: incl(),
  }},

  // ── Planning ───────────────────────────────────────────────────────────────
  { feature: 'Sprint Forecast Matrix', group: 'Planning',
    detail: 'See who can take what before sprint planning, not after.',
    cells: { free: excl(), team: incl(), business: incl(), enterprise: incl() } },
  { feature: 'Capacity tracker + Assign Work', group: 'Planning', cells: {
      free: excl(), team: incl(), business: incl(), enterprise: incl(),
  }},
  { feature: 'Department + team rollups', group: 'Planning', cells: {
      free: excl(), team: incl(), business: incl(), enterprise: incl(),
  }},

  // ── AI ─────────────────────────────────────────────────────────────────────
  { feature: 'Excel mapping AI', group: 'AI',
    detail: 'AI maps your spreadsheet columns to Avium fields — no manual setup.',
    cells: { free: excl(), team: incl(), business: incl(), enterprise: incl() } },
  { feature: 'Avium AI Briefing', group: 'AI',
    detail: 'Plain-English narrative on top of Avium Signals.',
    cells: { free: excl(), team: excl(), business: incl(), enterprise: incl() } },
  { feature: 'Ask Avium', group: 'AI',
    detail: 'Natural-language queries against your own data.',
    cells: { free: excl(), team: excl(), business: incl(), enterprise: incl() } },
  { feature: 'Sprint Risk Forecast + Sprint Review Draft + Data-Quality Fixes', group: 'AI', cells: {
      free: excl(), team: excl(), business: incl(), enterprise: incl(),
  }},
  { feature: 'Reports narrative', group: 'AI', cells: {
      free: excl(), team: excl(), business: incl(), enterprise: incl(),
  }},

  // ── Sync & Export ──────────────────────────────────────────────────────────
  { feature: 'Two-way Jira sync (write-back)', group: 'Sync & Export',
    detail: 'Push status, assignee, sprint, story points, fix versions, and more back to Jira.',
    cells: { free: excl(), team: excl(), business: incl(), enterprise: incl() } },
  { feature: 'Data export (Excel + CSV)', group: 'Sync & Export', cells: {
      free: excl(), team: excl(), business: incl(), enterprise: incl(),
  }},
  { feature: 'Custom fields + branding', group: 'Sync & Export', cells: {
      free: excl(), team: excl(), business: incl(), enterprise: incl(),
  }},

  // ── Enterprise (most are roadmap today; SSO etc. ship with the Enterprise rollout) ─
  { feature: 'Real-time sync — changes land in seconds', group: 'Enterprise',
    detail: 'Your tool pushes events to Avium the moment work changes; scheduled sync stays on as the safety net. Consent-gated, removable anytime.',
    cells: { free: excl(), team: excl(), business: excl(), enterprise: incl() } },
  { feature: 'Avium Automations — on event, do X', group: 'Enterprise',
    detail: 'Rules that notify the right person or stage the recommended move the moment a risk is detected — plus scheduled digests you compose.',
    cells: { free: excl(), team: excl(), business: excl(), enterprise: incl() } },
  { feature: 'SSO / SAML', group: 'Enterprise', cells: {
      free: excl(), team: excl(), business: excl(), enterprise: future(),
  }},
  { feature: 'Priority support', group: 'Enterprise', cells: {
      free: excl(), team: excl(), business: excl(), enterprise: future(),
  }},
  { feature: 'Dedicated onboarding', group: 'Enterprise', cells: {
      free: excl(), team: excl(), business: excl(), enterprise: future(),
  }},
  { feature: 'Custom contract terms (DPA, MSA)', group: 'Enterprise', cells: {
      free: excl(), team: excl(), business: excl(), enterprise: future(),
  }},
];

// ── Per-tier projections (computed) ─────────────────────────────────────────

/** Short list of "what you get at this tier" — the included rows for the
 *  current tier, used on BillingPage's tier cards. Picks the first N by
 *  display order; the comparison table is for the full picture. */
export function getTierHighlights(plan: PlanName, limit = 6): string[] {
  return TIER_FEATURE_ROWS
    .filter(r => r.cells[plan].included)
    .slice(0, limit)
    .map(r => r.cells[plan].note ? `${r.feature} (${r.cells[plan].note})` : r.feature);
}

/** Feature rows grouped for rendering the comparison table by section. */
export function getTierFeatureGroups(): Array<{ group: FeatureGroup; rows: TierFeatureRow[] }> {
  const groups: FeatureGroup[] = ['Foundations', 'Avium Signals', 'Planning', 'AI', 'Sync & Export', 'Enterprise'];
  return groups.map(group => ({
    group,
    rows: TIER_FEATURE_ROWS.filter(r => r.group === group),
  }));
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Normalize an arbitrary plan string to a known tier (defaults to free). */
export function toPlanName(plan: string | null | undefined): PlanName {
  return plan && (PLAN_ORDER as string[]).includes(plan) ? (plan as PlanName) : 'free';
}

export function tierMeta(plan: string | null | undefined): TierMeta {
  return TIER_META[toPlanName(plan)];
}

/** The next tier up, or null if already at the top. */
export function nextTier(plan: string | null | undefined): TierMeta | null {
  const idx = PLAN_ORDER.indexOf(toPlanName(plan));
  return idx >= 0 && idx < PLAN_ORDER.length - 1 ? TIER_META[PLAN_ORDER[idx + 1]] : null;
}

/** The lowest tier that unlocks a given feature, for "Upgrade to X" copy. */
export function tierForFeature(feature: keyof TierMeta['features']): TierMeta {
  return TIER_META[PLAN_ORDER.find(p => TIER_META[p].features[feature]) ?? 'business'];
}

/** Format a numeric/nullable limit for display. */
export function fmtLimit(limit: number | null): string {
  if (limit === null) return 'Unlimited';
  return limit.toLocaleString();
}
